import { useCallback, useEffect, useState } from 'react';
import { Scale, FileText, Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';

type Any = Record<string, any>;

interface Props {
  akte: Any;
  onChanged?: () => void;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Keine' },
  { value: 'anfrage_erhalten', label: 'Anfrage erhalten' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung' },
  { value: 'entwurf_erstellt', label: 'Entwurf erstellt' },
  { value: 'versendet', label: 'Versendet' },
  { value: 'erledigt', label: 'Erledigt' },
];

const eur = (v: any) =>
  v == null || v === '' ? '–' : `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fdate = (v: any) => (v ? new Date(v).toLocaleDateString('de-DE') : '–');
const yn = (v: any) => (v === true ? 'Ja' : v === false ? 'Nein' : '–');

// Format-Code: 't' text, 'e' euro, 'd' datum, 'b' bool, 'n' zahl
type Fmt = 't' | 'e' | 'd' | 'b' | 'n';
type Feld = [string, string, Fmt?];

const GRUPPEN: { titel: string; felder: Feld[] }[] = [
  {
    titel: 'Akte & Auftrag',
    felder: [
      ['Aktennummer', 'akten_nr'],
      ['Gutachten-Az.', 'gutachten_aktenzeichen'],
      ['Auftragsart', 'auftragsart'],
      ['Auftragstyp', 'auftragstyp_sv'],
      ['Status', 'status'],
      ['Auftragsdatum', 'auftragsdatum', 'd'],
      ['Schadendatum', 'schadendatum', 'd'],
      ['Besichtigung', 'besichtigungsdatum', 'd'],
      ['Besichtigt durch', 'besichtigt_durch'],
      ['Fertig bis', 'fertigzustellen_bis', 'd'],
      ['Erteilt durch', 'erteilt_durch'],
      ['Vermittelt durch', 'vermittelt_durch'],
      ['Sachverständiger', 'gutachten_sv'],
      ['WinValue-UID', 'winvalue_uid'],
      ['Letzter Sync', 'winvalue_last_sync', 'd'],
    ],
  },
  {
    titel: 'Anspruchsteller / Kunde',
    felder: [
      ['Name', 'kunde_name'],
      ['Anspruchsteller', 'anspruchsteller_name'],
      ['Straße', 'anspruchsteller_strasse'],
      ['PLZ', 'anspruchsteller_plz'],
      ['Ort', 'anspruchsteller_ort'],
      ['Telefon', 'kunde_telefon'],
      ['E-Mail', 'kunde_email'],
      ['Geburtsdatum', 'anspruchsteller_geburtsdatum'],
    ],
  },
  {
    titel: 'Fahrer',
    felder: [
      ['Name', 'fahrer_name'],
      ['Straße', 'fahrer_strasse'],
      ['PLZ', 'fahrer_plz'],
      ['Ort', 'fahrer_ort'],
      ['Telefon', 'fahrer_telefon'],
      ['E-Mail', 'fahrer_email'],
      ['= Anspruchsteller', 'fahrer_ist_anspruchsteller', 'b'],
    ],
  },
  {
    titel: 'Rechtsanwalt',
    felder: [
      ['Kanzlei/Name', 'anwalt_name'],
      ['Straße', 'anwalt_adresse'],
      ['PLZ', 'anwalt_plz'],
      ['Ort', 'anwalt_ort'],
      ['Telefon', 'anwalt_telefon'],
      ['E-Mail', 'anwalt_email'],
      ['Aktenzeichen', 'anwalt_aktenzeichen'],
    ],
  },
  {
    titel: 'Gegnerische Versicherung',
    felder: [
      ['Name', 'versicherung'],
      ['Straße', 'versicherung_adresse'],
      ['PLZ', 'versicherung_plz'],
      ['Ort', 'versicherung_ort'],
      ['Telefon', 'versicherung_telefon'],
      ['E-Mail', 'versicherung_email'],
      ['Vers.-Nummer', 'versicherungsnummer'],
      ['Schaden-Nr.', 'schadennummer'],
    ],
  },
  {
    titel: 'Unfallgegner',
    felder: [
      ['Name', 'gegner_name'],
      ['Straße', 'gegner_adresse'],
      ['PLZ', 'gegner_plz'],
      ['Ort', 'gegner_ort'],
      ['Kennzeichen', 'kennzeichen_gegner'],
    ],
  },
  {
    titel: 'Werkstatt',
    felder: [
      ['Name', 'werkstatt_name'],
      ['Straße', 'werkstatt_adresse'],
      ['PLZ', 'werkstatt_plz'],
      ['Ort', 'werkstatt_ort'],
      ['Telefon', 'werkstatt_telefon'],
      ['E-Mail', 'werkstatt_email'],
    ],
  },
  {
    titel: 'Unfall & Schaden',
    felder: [
      ['Unfalldatum', 'unfalldatum', 'd'],
      ['Schadentag', 'schadentag', 'd'],
      ['Unfallort', 'unfallort'],
      ['Schadenort', 'schadenort'],
      ['Straße', 'schadenort_strasse'],
      ['Schadenart', 'schadenart'],
      ['Hergang', 'unfallhergang'],
      ['Polizei aufgenommen', 'polizei_aufgenommen', 'b'],
      ['Polizei-Az.', 'polizei_aktenzeichen'],
      ['Haftungsquote', 'haftungsquote', 'n'],
      ['Selbstbeteiligung', 'selbstbeteiligung', 'e'],
    ],
  },
  {
    titel: 'Fahrzeug',
    felder: [
      ['Marke', 'fahrzeug_marke'],
      ['Modell', 'fahrzeug_modell'],
      ['Submodell', 'fahrzeug_submodell'],
      ['Kennzeichen', 'fahrzeug_kennzeichen'],
      ['FIN', 'fahrzeug_vin'],
      ['Erstzulassung', 'fahrzeug_ez'],
      ['Baujahr', 'fahrzeug_baujahr', 'n'],
      ['Laufleistung (km)', 'fahrzeug_km', 'n'],
      ['Farbe', 'fahrzeug_farbe'],
      ['HSN', 'fahrzeug_hsn'],
      ['TSN', 'fahrzeug_tsn'],
      ['Leistung (kW)', 'fahrzeug_kw', 'n'],
      ['Hubraum (ccm)', 'fahrzeug_ccm', 'n'],
      ['HU', 'fahrzeug_hu', 'd'],
      ['Fahrbereit', 'fahrzeug_fahrbereit', 'b'],
      ['Totalschaden', 'fahrzeug_totalschaden', 'b'],
      ['Standort', 'fahrzeug_standort'],
    ],
  },
  {
    titel: 'Beträge & Bewertung',
    felder: [
      ['Reparaturkosten netto', 'reparaturkosten_netto', 'e'],
      ['Reparaturkosten brutto', 'reparaturkosten_brutto', 'e'],
      ['Lohnkosten netto', 'lohnkosten_netto', 'e'],
      ['Lackkosten netto', 'lackkosten_netto', 'e'],
      ['Ersatzteile netto', 'ersatzteilkosten_netto', 'e'],
      ['Zusatzkosten netto', 'zusatzkosten_netto', 'e'],
      ['Wiederbeschaffungswert', 'wiederbeschaffungswert', 'e'],
      ['WBW netto', 'wiederbeschaffungswert_netto', 'e'],
      ['Restwert', 'restwert', 'e'],
      ['Merkantiler Minderwert', 'minderwert', 'e'],
      ['Nutzungsausfall', 'nutzungsausfall_betrag', 'e'],
      ['Reparaturdauer (Tage)', 'reparaturdauer_tage', 'n'],
      ['Vorauss. Rep.-Dauer', 'voraussichtliche_reparaturdauer', 'n'],
      ['Kostenvoranschlag', 'kostenvoranschlag', 'e'],
      ['MwSt.-Satz', 'mwst_satz', 'n'],
    ],
  },
  {
    titel: 'Rechnung',
    felder: [
      ['Rechnungs-Nr.', 'rechnung_nr'],
      ['Rechnungsdatum', 'rechnung_datum', 'd'],
      ['Rechnungsbetrag', 'rechnung_betrag', 'e'],
      ['Bezahlt', 'rechnung_bezahlt', 'b'],
      ['Bezahlt am', 'rechnung_bezahlt_am', 'd'],
      ['Auslagen', 'auslagen', 'e'],
    ],
  },
];

function fmtVal(akte: Any, key: string, f?: Fmt): string {
  const v = akte?.[key];
  if (f === 'e') return eur(v);
  if (f === 'd') return fdate(v);
  if (f === 'b') return yn(v);
  return v == null || v === '' ? '–' : String(v);
}

export default function StellungnahmeUebersicht({ akte, onChanged }: Props) {
  const [status, setStatus] = useState<string>(akte?.stellungnahme_status ?? '');
  const [frist, setFrist] = useState<string>(akte?.schriftsatzfrist ?? '');
  const [auftraggeber, setAuftraggeber] = useState<string>(akte?.stellungnahme_auftraggeber ?? '');
  const [repGekuerzt, setRepGekuerzt] = useState<boolean>(!!akte?.rep_kosten_gekuerzt);
  const [repBetrag, setRepBetrag] = useState<string>(akte?.rep_kosten_kuerzung_betrag ?? '');
  const [repGrund, setRepGrund] = useState<string>(akte?.rep_kosten_kuerzung_grund ?? '');
  const [nachbesichtigung, setNachbesichtigung] = useState<boolean>(!!akte?.nachbesichtigung_gefordert);
  const [reguProblem, setReguProblem] = useState<string>(akte?.regulierungsproblem ?? '');

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [schreiben, setSchreiben] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);

  useEffect(() => {
    setStatus(akte?.stellungnahme_status ?? '');
    setFrist(akte?.schriftsatzfrist ?? '');
    setAuftraggeber(akte?.stellungnahme_auftraggeber ?? '');
    setRepGekuerzt(!!akte?.rep_kosten_gekuerzt);
    setRepBetrag(akte?.rep_kosten_kuerzung_betrag ?? '');
    setRepGrund(akte?.rep_kosten_kuerzung_grund ?? '');
    setNachbesichtigung(!!akte?.nachbesichtigung_gefordert);
    setReguProblem(akte?.regulierungsproblem ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akte?.id]);

  useEffect(() => {
    let aktiv = true;
    if (!akte?.id) return;
    (async () => {
      const [doc, tl] = await Promise.all([
        supabase
          .from('dokumente')
          .select('id, titel, dateiname, dokument_typ, erstellt_am')
          .eq('akte_id', akte.id)
          .in('dokument_typ', ['letter', 'letter_statement'])
          .is('geloescht_am', null)
          .order('erstellt_am', { ascending: false }),
        supabase
          .from('stellungnahme_timeline_events')
          .select('*')
          .eq('akte_id', akte.id)
          .order('datum', { ascending: false }),
      ]);
      if (!aktiv) return;
      setSchreiben(doc.data ?? []);
      setTimeline(tl.data ?? []);
    })();
    return () => {
      aktiv = false;
    };
  }, [akte?.id]);

  const save = useCallback(
    async (patch: Record<string, any>) => {
      if (!akte?.id) return;
      setSaving(true);
      setMsg(null);
      const { error } = await supabase.from('akten').update(patch).eq('id', akte.id);
      setSaving(false);
      if (error) {
        setMsg('Fehler: ' + error.message);
        return;
      }
      setMsg('Gespeichert.');
      onChanged?.();
      setTimeout(() => setMsg(null), 2000);
    },
    [akte?.id, onChanged],
  );

  const card = 'bg-white rounded-xl border border-gray-200 p-5';
  const grpFelder = (felder: Feld[]) =>
    felder.filter(([, key]) => {
      const v = akte?.[key];
      return v !== null && v !== undefined && v !== '';
    });

  return (
    <div className="space-y-4">
      {/* Stellungnahme & Kürzung — editierbar */}
      <div className={card}>
        <div className="flex items-center gap-2 mb-4">
          <Scale className="w-5 h-5 text-gray-700" />
          <h2 className="text-base font-semibold text-gray-800">Stellungnahme & Kürzung</h2>
          {saving && <span className="text-xs text-gray-400">speichert…</span>}
          {msg && <span className="text-xs text-gray-500">{msg}</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-500">Status</span>
            <select
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={status}
              onChange={(e) => { setStatus(e.target.value); save({ stellungnahme_status: e.target.value || null }); }}
            >
              {STATUS_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-500">Schriftsatzfrist</span>
            <input type="date" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={frist ? String(frist).slice(0, 10) : ''}
              onChange={(e) => { setFrist(e.target.value); save({ schriftsatzfrist: e.target.value || null }); }} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-500">Auftraggeber</span>
            <select className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={auftraggeber}
              onChange={(e) => { setAuftraggeber(e.target.value); save({ stellungnahme_auftraggeber: e.target.value || null }); }}>
              <option value="">– bitte wählen –</option>
              <option value="kunde">Kunde</option>
              <option value="anwalt">Anwalt</option>
            </select>
          </label>
          <div className="block">
            <span className="text-xs font-medium text-gray-500">Honorarkürzung (Versicherung)</span>
            <div className="mt-1 text-sm text-gray-800">{eur(akte?.kuerzung_betrag)}</div>
            {akte?.kuerzung_grund && <p className="text-xs text-gray-500 mt-0.5">{akte.kuerzung_grund}</p>}
          </div>
          <div className="sm:col-span-2 flex flex-col gap-2 justify-end pb-1">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={repGekuerzt}
                onChange={(e) => { setRepGekuerzt(e.target.checked); save({ rep_kosten_gekuerzt: e.target.checked }); }} />
              Reparaturkosten gekürzt
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={nachbesichtigung}
                onChange={(e) => { setNachbesichtigung(e.target.checked); save({ nachbesichtigung_gefordert: e.target.checked }); }} />
              Nachbesichtigung gefordert
            </label>
          </div>
          {repGekuerzt && (
            <>
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Kürzungsbetrag Rep. (€)</span>
                <input type="number" step="0.01" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={repBetrag} onChange={(e) => setRepBetrag(e.target.value)}
                  onBlur={() => save({ rep_kosten_kuerzung_betrag: repBetrag === '' ? null : Number(repBetrag) })} />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs font-medium text-gray-500">Grund der Kürzung</span>
                <input className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={repGrund} onChange={(e) => setRepGrund(e.target.value)}
                  onBlur={() => save({ rep_kosten_kuerzung_grund: repGrund.trim() || null })} />
              </label>
            </>
          )}
          <div className="sm:col-span-3">
            <span className="text-xs font-medium text-gray-500">Regulierungsproblem / Sonstiges</span>
            <textarea className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[60px]"
              value={reguProblem} onChange={(e) => setReguProblem(e.target.value)}
              onBlur={() => save({ regulierungsproblem: reguProblem.trim() || null })} />
          </div>
        </div>
      </div>

      {/* Alle Datenfelder, gruppiert */}
      {GRUPPEN.map((g) => {
        const felder = grpFelder(g.felder);
        if (felder.length === 0) return null;
        return (
          <div key={g.titel} className={card}>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">{g.titel}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
              {felder.map(([label, key, f]) => (
                <div key={key} className="flex justify-between gap-3 text-sm border-b border-gray-50 py-0.5">
                  <span className="text-gray-500">{label}</span>
                  <span className="text-gray-900 font-medium text-right">{fmtVal(akte, key, f)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Schreiben der Gegenseite */}
      <div className={card}>
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" /> Schreiben der Versicherung / Gegenseite
        </h3>
        {schreiben.length === 0 ? (
          <p className="text-sm text-gray-500">Keine Korrespondenz-Dokumente gefunden.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {schreiben.map((d) => (
              <li key={d.id} className="py-2 text-sm text-gray-700 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" />
                <span className="truncate">{d.titel || d.dateiname}</span>
                <span className="text-xs text-gray-400 ml-auto">{d.erstellt_am ? new Date(d.erstellt_am).toLocaleDateString('de-DE') : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Chronologie */}
      <div className={card}>
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" /> Chronologie
        </h3>
        {timeline.length === 0 ? (
          <p className="text-sm text-gray-500">Noch keine Ereignisse.</p>
        ) : (
          <ul className="space-y-2">
            {timeline.map((e) => (
              <li key={e.id} className="text-sm text-gray-700 flex gap-2">
                <span className="text-xs text-gray-400 w-24 shrink-0">{e.datum ? new Date(e.datum).toLocaleDateString('de-DE') : ''}</span>
                <span><span className="font-medium">{e.ereignis_typ}</span>{e.kurzbetreff ? ` · ${e.kurzbetreff}` : ''} (v{e.version})</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
